import { useEffect, useState } from "react"
import type { WidgetInstance } from "lib/widget-types"
import { widgetRegistry } from "lib/widget-registry"
import { loadLayout, saveLayout } from "lib/dashboard-layout"
import { NotePanel } from "components/notes/NotePanel"
import { AgentChatPanel } from "./agent-chat-panel"

interface CurrentUser {
  first_name?: string
  last_name?: string
  email?: string
  username?: string
  profile_photo_url?: string | null
}

interface DashboardRendererProps {
  currentUser?: CurrentUser | null
}

const TOP_ROW_TYPES = new Set(["welcomeKpi", "quickActions"])

export function DashboardRenderer({ currentUser }: DashboardRendererProps) {
  const [layout, setLayout] = useState<WidgetInstance[]>([])
  const [showNotePanel, setShowNotePanel] = useState(false)
  const [noteRefreshKey, setNoteRefreshKey] = useState(0)
  const [agendaRefreshKey, setAgendaRefreshKey] = useState(0)

  useEffect(() => {
    const loadedLayout = loadLayout()
    setLayout(loadedLayout)
  }, [])

  useEffect(() => {
    if (layout.length > 0) {
      saveLayout(layout)
    }
  }, [layout])

  const enabledWidgets = layout.filter((widget) => widget.enabled !== false)
  const topRowWidgets = enabledWidgets.filter((w) => TOP_ROW_TYPES.has(w.type))
  const mainWidgets = enabledWidgets.filter((w) => !TOP_ROW_TYPES.has(w.type))

  function buildWidgetProps(widget: WidgetInstance) {
    const widgetWithUser = {
      ...widget,
      config: {
        ...widget.config,
        userName: currentUser?.first_name || currentUser?.username || widget.config?.userName || "Utente",
        userFirstName: currentUser?.first_name,
        userLastName: currentUser?.last_name,
        userUsername: currentUser?.username,
        userProfilePhotoUrl: currentUser?.profile_photo_url,
      },
    }

    let extraProps: Record<string, unknown> = {}
    if (widget.type === "notes") {
      extraProps = { onOpenNote: () => setShowNotePanel(true) }
    } else if (widget.type === "todayAgenda") {
      extraProps = { refreshKey: agendaRefreshKey }
    } else if (widget.type === "quickActions") {
      extraProps = { onEventCreated: () => setAgendaRefreshKey((prev) => prev + 1) }
    }

    return { widgetWithUser, extraProps }
  }

  function renderWidget(widget: WidgetInstance, extraClassName = "") {
    const WidgetComponent = widgetRegistry[widget.type]
    if (!WidgetComponent) return null

    const { widgetWithUser, extraProps } = buildWidgetProps(widget)

    return (
      <div
        key={widget.type === "notes" ? `${widget.id}-${noteRefreshKey}` : widget.id}
        className={`relative min-w-0 ${extraClassName}`}
      >
        <WidgetComponent widget={widgetWithUser} {...extraProps} />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Top row: Welcome+KPI (span=2) + Quick Actions (span=1) */}
      {topRowWidgets.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4" style={{ minHeight: "200px" }}>
          {topRowWidgets.map((widget) => {
            const spanClass = widget.span === 2 ? "xl:col-span-2" : "xl:col-span-1"
            return renderWidget(widget, spanClass)
          })}
        </div>
      )}

      {/* Wrapper: widget grid (left) + embedded chat (right) */}
      <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        {/* Left: 2×2 widget grid */}
        <div
          className="grid min-w-0 grid-cols-1 md:grid-cols-2 gap-3"
          style={{ gridAutoRows: "240px" }}
        >
          {mainWidgets.map((widget) => renderWidget(widget))}
        </div>

        {/* Right: Agent Chat embedded */}
        <div id="dashboard-agent-chat" className="min-w-0 h-[min(600px,85svh)] min-h-[420px] xl:h-[492px]">
          <AgentChatPanel isOpen={true} onClose={() => {}} mode="embedded" />
        </div>
      </div>

      {/* NotePanel side-panel */}
      {showNotePanel && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setShowNotePanel(false)
              setNoteRefreshKey((prev) => prev + 1)
            }}
          />
          <div className="absolute right-0 top-0 h-full w-full max-w-[340px] shadow-lg">
            <NotePanel
              onClose={() => {
                setShowNotePanel(false)
                setNoteRefreshKey((prev) => prev + 1)
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
